import { Box, Chip, Collapse, Tooltip, Typography } from '@mui/material';
import { Warning } from '@vy/ui-design-system';
import type { DisconnectedMeter, DisconnectedMeterBreakpoint } from '@vy/types';

const BREAKPOINT_LABELS: Record<DisconnectedMeterBreakpoint, string> = {
  noVariant: 'Not linked to any plan',
  noPlan: 'Plan has no contract',
  noContract: 'Plan has no contract',
};

const CHAIN_STEPS: { key: string; label: string }[] = [
  { key: 'meter', label: 'Meter' },
  { key: 'variant', label: 'Plan' },
  { key: 'contract', label: 'Contract' },
];

function isBroken(step: string, breakpoint: DisconnectedMeterBreakpoint): boolean {
  if (step === 'variant' && breakpoint === 'noVariant') return true;
  if (step === 'contract' && (breakpoint === 'noPlan' || breakpoint === 'noContract')) return true;
  return false;
}

function MeterChain({ breakpoint }: { breakpoint: DisconnectedMeterBreakpoint }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5
      }}>
      {CHAIN_STEPS.map((step, i) => {
        const broken = isBroken(step.key, breakpoint);
        return (
          <Box
            key={step.key}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5
            }}>
            {i > 0 && (
              <Typography variant="caption" color={broken ? 'error.main' : 'text.secondary'} sx={{
                lineHeight: 1
              }}>
                {broken ? '✕' : '→'}
              </Typography>
            )}
            <Typography
              variant="caption"
              color={broken ? 'error.main' : 'text.secondary'}
              sx={{
                fontWeight: broken ? 700 : 400,
                textDecoration: broken ? 'line-through' : 'none'
              }}>
              {step.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

type Props = {
  disconnectedMeters: DisconnectedMeter[];
};

export function DisconnectedMeterWarnings({ disconnectedMeters, open }: Props & { open: boolean }) {
  return (
    <Collapse in={open && disconnectedMeters.length > 0} unmountOnExit>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1.5,
          p: 1.5,
          borderRadius: 1,
          bgcolor: 'warning.50',
          border: '1px solid',
          borderColor: 'warning.200',
        }}
      >
        <Box
          sx={{
            width: "100%",
            mb: 0.5,
            display: "flex",
            alignItems: "center",
            gap: 0.5
          }}>
          <Warning width={14} height={14} viewBox="0 0 24 24" color="warning" style={{ color: '#ed6c02' }} />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: "warning.dark"
            }}>
            {disconnectedMeters.length} meter{disconnectedMeters.length !== 1 ? 's' : ''} not fully connected to a contract
          </Typography>
        </Box>
        {disconnectedMeters.map((m) => (
          <Tooltip
            key={m.meterName}
            title={
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mb: 0.5
                  }}>
                  {BREAKPOINT_LABELS[m.breakpoint]}
                </Typography>
                <MeterChain breakpoint={m.breakpoint} />
              </Box>
            }
            placement="bottom-start"
          >
            <Chip
              label={m.meterName}
              size="small"
              variant="outlined"
              color="warning"
              sx={{ cursor: 'default', fontSize: 12 }}
            />
          </Tooltip>
        ))}
      </Box>
    </Collapse>
  );
}
